/**
 * The one place the room detail path is spelled (D4c2).
 *
 * Every surface that shows a room — the room map, a task's links, a
 * report's links — routes through this, so the path cannot drift into
 * three slightly different spellings. Same contract as
 * ``getWorkReportDetailPath``, deliberately: one convention for one
 * kind of problem.
 *
 * Returns ``null`` when there is no id. Callers MUST treat null as
 * "render the text, not a link": that is what keeps a half-loaded row
 * from producing a link to ``/admin/rooms/undefined``, which would look
 * clickable and land on a 404.
 *
 * Only a real Room id is accepted. Nothing is ever derived from the
 * name, the unit or any other field.
 */
export function getRoomDetailPath(roomId) {
  const id = typeof roomId === "string" ? roomId.trim() : roomId;
  if (!id) return null;
  return `/admin/rooms/${id}`;
}

/** Accessible name for the "open this room" link. Names the room, so a
 * map of twelve tiles reads as twelve distinct destinations rather than
 * twelve controls called "Öffnen". */
export function openRoomLabel(name) {
  const n = (name || "").trim();
  return n ? `Zimmer öffnen: ${n}` : "Zimmer öffnen";
}

/**
 * German copy for the backend's ``planning_state``.
 *
 * ``planning_state`` is the BACKEND source of truth (computed by
 * ``occupancy_service.compute_room_planning``). This maps it to display
 * text and a badge tone — it does NOT recompute the state, exactly as
 * ``roomPlanningLabel`` in ``utils/unitOccupancyStatus`` does not.
 */
export const ROOM_PLANNING_STATE_LABELS = {
  occupied: "Belegt",
  occupied_then_reserved: "Belegt · Nachmieter reserviert",
  free: "Frei",
  free_then_reserved: "Frei · reserviert",
};

/** Badge tone per planning state, matching the vocabulary
 * ``occupancyStatusBadgeTone`` already uses on the unit pages: occupied
 * green, reserved blue, free slate. */
export const ROOM_PLANNING_STATE_TONES = {
  occupied: "green",
  occupied_then_reserved: "green",
  free: "slate",
  free_then_reserved: "blue",
};
